import { Component, input, output, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { controlEvents } from '../../../utils/control-events.util';

/**
 * Ezfy Custom Datepicker Component
 *
 * A customizable datepicker component that follows the Ezfy design system.
 * Built on top of Angular Material's datepicker with custom styling and theming.
 *
 * @example
 * ```typescript
 * // In your component
 * import { FormControl } from '@angular/forms';
 *
 * dateControl = new FormControl();
 * dateConfig = {
 *   variant: 'primary',
 *   size: 'sm',
 *   placeholder: 'Select a date',
 *   label: 'Date of Birth',
 *   required: true,
 *   showIcon: true
 * };
 * ```
 *
 * ```html
 * <app-datepicker
 *   [control]="dateControl"
 *   [config]="dateConfig"
 *   (dateChange)="onDateSelected($event)">
 * </app-datepicker>
 * ```
 */

export type DatepickerVariant = 'primary' | 'secondary' | 'outline' | 'glass';
export type DatepickerSize = 'sm' | 'md' | 'lg';

export interface DatepickerConfig {
  variant?: DatepickerVariant;
  size?: DatepickerSize;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  min?: Date;
  max?: Date;
  startView?: 'month' | 'year' | 'multi-year';
  label?: string;
  helperText?: string;
  errorText?: string;
  showIcon?: boolean;
  iconPosition?: 'left' | 'right';
  panelClass?: string; // Custom panel class for calendar overlay
}

@Component({
  selector: 'app-datepicker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule
  ],
  templateUrl: './datepicker.html',
  styleUrl: './datepicker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-PT' }
  ]
})
export class DatepickerComponent {
  // Inputs using Angular 20 input() function
  readonly control = input<FormControl>(new FormControl());
  readonly config = input<DatepickerConfig>({
    variant: 'primary',
    size: 'sm',
    placeholder: 'Selecionar data',
    showIcon: true,
    iconPosition: 'right'
  });

  // Outputs using Angular 20 output() function
  readonly dateChange = output<Date>();
  readonly opened = output<void>();
  readonly closed = output<void>();

  // Computed values using computed() function
  readonly datepickerVariant = computed(() => this.config().variant || 'primary');
  readonly datepickerSize = computed(() => this.config().size || 'md');
  readonly isDisabled = computed(() => this.config().disabled || false);
  readonly isReadonly = computed(() => this.config().readonly || false);
  readonly isRequired = computed(() => this.config().required || false);
  readonly showIcon = computed(() => this.config().showIcon !== false);
  readonly iconPosition = computed(() => this.config().iconPosition || 'right');
  readonly placeholderText = computed(() => this.config().placeholder || 'Selecionar data');
  readonly labelText = computed(() => this.config().label || '');
  readonly helperText = computed(() => this.config().helperText || '');
  readonly errorText = computed(() => this.config().errorText || '');
  // The control is not a signal: follow its events so hasError tracks touched and validity changes.
  private readonly controlEvents = controlEvents(this.control);
  readonly hasError = computed(() => {
    this.controlEvents();
    return this.control()?.invalid && this.control()?.touched;
  });
  readonly panelClass = computed(() => this.config().panelClass || 'datepicker__panel');

  // Computed CSS classes
  readonly datepickerClasses = computed(() => {
    const variant = this.datepickerVariant();
    const size = this.datepickerSize();
    const disabled = this.isDisabled();
    const readonly = this.isReadonly();
    const error = this.hasError();

    return {
      [`datepicker--${variant}`]: true,
      [`datepicker--${size}`]: true,
      'datepicker--disabled': disabled,
      'datepicker--readonly': readonly,
      'datepicker--error': error
    };
  });

  readonly inputClasses = computed(() => {
    const position = this.iconPosition();
    return {
      'datepicker__input--icon-left': position === 'left',
      'datepicker__input--icon-right': position === 'right'
    };
  });

  // Methods
  onDateChange(date: Date | null): void {
    //console.log('Datepicker: onDateChange called with:', date);
    if (date) {
      this.dateChange.emit(date);
    }
  }

  onOpened(): void {
    //console.log('Datepicker: Calendar opened');
    this.opened.emit();
  }

  onClosed(): void {
    //console.log('Datepicker: Calendar closed');
    this.closed.emit();
  }

  // Utility method to check if date is selected
  isSelected(): boolean {
    return this.control()?.value !== null && this.control()?.value !== undefined;
  }
}
